/**
 * Simplified Chinese for the site (lib/i18n.tsx). Key = the English shown on the site; value = the Chinese.
 * A line missing here shows in English, so adding a translation never breaks anything.
 *
 * FOR THE REVIEWER: first drafts, to be checked by someone at March. Change the Chinese on the right only;
 * keep {placeholders} exactly as written (they are filled with numbers, dates and codes) and keep "\n"
 * where a column header breaks onto two lines. Part codes, PO numbers and product names (Solstice,
 * Pacifica Marine, Sunbrella, Alto…) stay in English on purpose.
 *
 * Glossary: please check these first, they recur everywhere.
 *   Frame 伞架 · Cover 伞布 · Base 伞座 · Fabric 面料 · Trim 饰边 · Protective cover 防护罩
 *   Stock / on hand 库存 · On order 已下单（未到货） · Forecast use 预测用量 · Need to order 需订购
 *   Order now 立即下单 · Ship sooner 需提前发货 · Top-up 补货 · Late PO 逾期 PO · Safety stock 安全库存
 *   PO 采购单 (kept as "PO" next to a number) · Container 货柜 · Lead time 交货周期 · Jurupa (warehouse name, kept)
 */
export const ZH: Record<string, string> = {
  // ─── menu and top bar ───
  "Every day": "日常",
  Planning: "计划",
  Help: "帮助",
  Admin: "管理",
  Home: "首页",
  "To order": "待订购",
  "for buying": "采购用",
  Arriving: "即将到货",
  "for receiving": "收货用",
  Forecast: "预测",
  Trends: "趋势",
  "what customers choose": "客户选购情况",
  "Yearly Demand": "年度需求",
  "Forecast accuracy": "预测准确度",
  "How this site works": "网站使用说明",
  "Data status": "数据状态",
  Search: "搜索",
  "Guide: how the app works": "使用指南",
  Main: "主菜单",
  "Open menu": "打开菜单",
  "Colour theme": "颜色主题",
  Light: "浅色",
  Dark: "深色",
  Auto: "自动",
  "Light theme": "浅色主题",
  "Dark theme": "深色主题",
  "Follow your computer's setting": "跟随电脑设置",
  viewer: "查看者",
  editor: "编辑者",
  admin: "管理员",
  factory: "工厂",
  loading: "加载中",
  "Sign out": "退出登录",
  "Stock syncs from NetSuite every hour": "库存每小时从 NetSuite 同步一次",
  "Updated {day}, {time}": "更新于 {day} {time}",
  "{n} overdue": "{n} 项逾期",
  "Search a part, PO or container": "搜索零件、PO 或货柜",
  "Part, PO or container, e.g. alto 9 bronze, 16802, CA-27013": "零件、PO 或货柜，例如 alto 9 bronze、16802、CA-27013",

  // ─── part categories and product lines ───
  Frames: "伞架",
  Covers: "伞布",
  Bases: "伞座",
  Fabric: "面料",
  Trim: "饰边",
  "Protective covers": "防护罩",
  "Solstice covers": "Solstice 伞布",
  "Pacifica Marine covers": "Pacifica Marine 伞布",
  "Sunbrella covers": "Sunbrella 伞布",
  "Other covers": "其他伞布",
  "{code} bases": "{code} 伞座",
  yd: "码",
  units: "件",

  // ─── dates ───
  "no date": "无日期",
  Jan: "1月", Feb: "2月", Mar: "3月", Apr: "4月", May: "5月", Jun: "6月",
  Jul: "7月", Aug: "8月", Sep: "9月", Oct: "10月", Nov: "11月", Dec: "12月",

  // ─── Home ───
  "Parts only.": "仅限零件。",
  "Finished umbrellas are in NetSuite. Nothing you do here changes NetSuite.": "成品伞在 NetSuite 中查看。在本网站的任何操作都不会更改 NetSuite。",
  Dismiss: "关闭",
  "not yet": "尚未运行",
  "not run yet": "尚未运行",
  "matches ({n} parts checked)": "一致（已核对 {n} 个零件）",
  "{n} parts differ": "{n} 个零件不一致",
  failed: "失败",
  "Stock from NetSuite {when}": "NetSuite 库存：{when}",
  "Forecast updated {when}": "预测更新：{when}",
  "NetSuite check: {result}": "NetSuite 核对：{result}",
  "Couldn't load stock and orders:": "无法加载库存和订单：",
  Buying: "采购",
  "Open To order →": "打开待订购 →",
  "Order now": "立即下单",
  parts: "个零件",
  "They'll run out before a new order could arrive": "新订单到货前就会断货",
  "Ship sooner": "需提前发货",
  deliveries: "批到货",
  "On order, but arriving after the shelf runs empty": "已下单，但到货时库存已用完",
  "Top-ups": "补货",
  "Can wait": "可以稍后",
  "Not counted: {n} parts Operations doesn't keep in stock ({a} would be in Order now, {b} in Top-ups). Nobody needs to buy them here.":
    "未计入：{n} 个运营部不备库存的零件（其中 {a} 个本应列入“立即下单”，{b} 个列入“补货”）。无需在此采购。",
  "Receiving at Jurupa": "Jurupa 收货",
  "Open Arriving →": "打开即将到货 →",
  Overdue: "逾期",
  "POs or containers": "个 PO 或货柜",
  "{late} late, {stalled} stalled.": "{late} 项逾期，{stalled} 项停滞。",
  "Most overdue: {ref}, due {date}": "逾期最久：{ref}，应于 {date} 到货",
  "Oldest line was due {date}": "最早一项应于 {date} 到货",
  "Arriving this week": "本周到货",
  "Next: {ref} on {date}": "下一批：{ref}，{date}",
  "Nothing else due this week": "本周没有其他到货",
  Stalled: "停滞",
  POs: "个 PO",
  "Over 30 days late and never shipped. Chase or close in NetSuite": "逾期超过 30 天且从未发货。请在 NetSuite 中跟进或关闭",
  "PO {ref}": "PO {ref}",

  // ─── Forecast ───
  View: "视图",
  Simple: "简洁",
  "More detail": "详细",
  "Every column, for planners.": "显示全部列，供计划人员使用。",
  Show: "显示",
  Parts: "零件",
  "Needs attention": "需关注",
  "Needs attention · {n} parts": "需关注 · {n} 个零件",
  "Late PO": "逾期 PO",
  "Top-up": "补货",
  "Over a year of stock": "库存超过一年",
  OK: "正常",
  "OK · 1 yr+ of stock": "正常 · 库存一年以上",
  "Filter parts": "筛选零件",
  Export: "导出",
  "✕ Back to needs attention": "✕ 返回需关注",
  "Couldn't load the forecast:": "无法加载预测：",
  "Loading…": "加载中…",
  Part: "零件",
  Status: "状态",
  "Current\nstock": "当前\n库存",
  "Forecast use": "预测用量",
  "red = more than stock": "红色 = 超出库存",
  "On order": "已下单",
  "not here yet": "尚未到货",
  "Runs out": "断货日期",
  "at the forecast rate": "按预测用量计算",
  "Need to\norder": "需\n订购",
  "Nothing needs attention in this category.": "此类别没有需要关注的零件。",
  "Nothing matches.": "没有匹配的结果。",
  "chase it · no order needed": "请跟进 · 无需下单",
  "Everything else · {n}": "其他 · {n}",
  "Show them": "显示",
  "Top-ups and OK parts. Pick a line to list its parts.": "补货和正常的零件。选择一个产品线查看其零件。",
  "Top-ups and OK parts.": "补货和正常的零件。",
  "Quantities in {unit}. Click a part for its stock chart and how the order is worked out.": "数量单位：{unit}。点击零件可查看库存图表及订购数量的计算方式。",
  "{n} parts with no stock, orders or forecast aren't listed.": "另有 {n} 个无库存、无订单、无预测的零件未列出。",
  "6+ months": "6 个月以上",

  // ─── stock story and on-order cells ───
  "No use expected": "预计无用量",
  "Empty now": "现已断货",
  "Runs out {date}": "{date} 断货",
  "stock back {date}": "{date} 恢复库存",
  "back after {month}": "{month}之后恢复",
  "nothing on order": "无在途订单",
  "Below safety stock now": "现已低于安全库存",
  "Below safety stock from {date}": "{date} 起低于安全库存",
  "In stock all 6 months": "未来 6 个月库存充足",
  late: "逾期",
  "{n} POs": "{n} 个 PO",
  "{n} more than current stock": "比当前库存多 {n}",
  "Current stock covers it": "当前库存足够",
  "+{n} more": "另有 {n} 批",

  // ─── change password ───
  "Change password": "修改密码",
  "Choose your password": "设置您的密码",
  "You signed in with a one-time password. Choose your own to open the site.": "您使用一次性密码登录。请设置自己的密码后再使用本网站。",
  "One-time password": "一次性密码",
  "Current password": "当前密码",
  "New password": "新密码",
  "(at least 10 characters)": "（至少 10 个字符）",
  "New password again": "再次输入新密码",
  "Saving…": "保存中…",
  "Password changed.": "密码已修改。",
  "The new passwords don't match.": "两次输入的新密码不一致。",
  "Current password is wrong": "当前密码错误",
  "At least 10 characters": "至少 10 个字符",
  "Can't be your email": "不能与邮箱相同",
  "Choose a password different from the current one": "新密码不能与当前密码相同",
  "This account signs in with Microsoft": "此账号使用 Microsoft 登录",
  "This account signs in with Microsoft. Change its password with Microsoft.": "此账号使用 Microsoft 登录，请在 Microsoft 修改密码。",
  "For signing in with email and password.": "用于邮箱和密码登录。",
  "Back to Home": "返回首页",
};
